/**
 * 回到这一页时自动把行动权要回来（§7.6）。
 *
 * 要解决的现象（用户报的原话）：「我本人账号在牌桌座位上了，但是我开了新标签页以后，
 * 我无法操作，只能看别人打，我自己也还在牌桌上。」
 *
 * 光靠常驻的「在这里继续」按钮不够 —— 那要求用户先意识到发生了什么。
 * 真正自然的做法是：**你把注意力放回这一页，权就回来。**
 *
 * ★ 两条规则，都要求页面此刻可见，所以口袋里的平板、后台被唤醒的旧标签页
 *   永远触发不了它们（那正是 §7.6 的立法理由）：
 *
 *   R1  hidden → visible：用户切回了这个标签页。
 *   R2  断线重连回来，而**掉线之前**这一页是持有行动权的那一条。
 *       这条是必需的：最常见的情形是「页面一直在前台，只是网抖了一下」——
 *       服务端会把权顺延给另一条连接，而 R1 永远不会触发，用户就卡死了。
 *
 * ★ R2 天然不会和别人打架：**别人明着抢我的权时我是连着的**，会先收到
 *   `actorChanged{isActor:false}`，依据随即清零，我不会反抢。
 *   只有「断线期间被服务端顺延走」才会触发它。
 *
 * ★ 连接不通时**绝不直接 send**：`claimActor` 在 `NEVER_DROP_MESSAGE_TYPES` 里
 *   （messages.ts:256），发不出去会留在 outbox，等下一次 welcome 被 `resendOutbox`
 *   补发 —— 如果那次重连发生在一小时后的后台唤醒，这条陈年 claim 就会替用户抢权。
 *   所以走 pending 标志，且页面一转 hidden 就丢弃它。
 *
 * 幂等：自己已经是 actor 时 `claimActor` 只回一条 `actorChanged{isActor:true}`，
 * 不会发 `sessionSuperseded`，所以多发几次没有副作用。
 *
 * ★ 逻辑与 React 分离（与 `roomDerive.ts` 同一手法）：核心是下面这个纯状态机，
 *   它不认识 hook 也不认识 store，因此可以在 node 环境里直接测 —— 而本项目的
 *   组件测试走的是 `renderToStaticMarkup`，跑不了 `useEffect`。
 */
import { useEffect, useRef } from 'react'
import { useNetStore } from '../transport/applyMessage.js'
import type { RoomLink } from './roomLink.js'

/** 状态机关心的那一点点连接状态。 */
export interface ActorClaimSnapshot {
  readonly phase: string
  readonly isActor: boolean
  readonly inRoom: boolean
}

export interface ActorClaimDeps {
  readonly claim: () => void
  readonly isVisible: () => boolean
}

export interface ActorClaimer {
  /** 可见性变化时调 */
  onVisibility(): void
  /** 每次连接状态变化时调 */
  onState(s: ActorClaimSnapshot): void
}

export function createActorClaimer(deps: ActorClaimDeps): ActorClaimer {
  let pending = false
  let wasActorBeforeDrop = false
  let prevPhase: string | null = null
  let last: ActorClaimSnapshot = { phase: 'idle', isActor: false, inRoom: false }

  const tryClaim = (s: ActorClaimSnapshot): void => {
    if (!s.inRoom || s.isActor) return
    if (s.phase !== 'live') {
      // 连接没通：记下意图，等回到 live 再发。绝不塞进 outbox。
      pending = true
      return
    }
    pending = false
    deps.claim()
  }

  return {
    onVisibility(): void {
      if (!deps.isVisible()) {
        // 转到后台：丢弃未发出的意图。一小时后回前台再替用户抢权是错的。
        pending = false
        return
      }
      tryClaim(last) // R1
    },

    onState(s: ActorClaimSnapshot): void {
      const was = prevPhase
      prevPhase = s.phase
      last = s

      // 离开 live 的那一刻快照「我当时是不是 actor」。
      // ★ 必须用这一刻的值：重连之后服务端会先发 actorChanged{false}，
      //   那时再读 isActor 永远是 false，R2 就永远不会触发。
      // ★ 而且必须排在「有权就清零」之前：掉线那一刻 store 里的 isActor 还是 true，
      //   先清零就把快照跳过去了。
      if (was === 'live' && s.phase !== 'live') {
        wasActorBeforeDrop = s.isActor
        return
      }

      /**
       * ★ 连接正常且手上有权 ⇒ 没有任何待办意图。
       *
       * 这一条同时管住两件事：
       *  · `pending` 泄漏：`tryClaim` 在 `s.isActor` 时直接 return 而不清 pending，
       *    于是「连接不通时记下的意图」会一直挂着，等很久以后某次重连替用户抢权。
       *  · 反抢的依据：之后如果被明着抢走（连接一直活着），wasActorBeforeDrop 已经是零，
       *    再掉线重连时的快照也会是 false，R2 不会触发。
       */
      if (s.phase === 'live' && s.isActor) {
        pending = false
        wasActorBeforeDrop = false
        return
      }

      // 回到 live：R2
      if (was !== null && was !== 'live' && s.phase === 'live') {
        if (!s.isActor && (pending || wasActorBeforeDrop) && deps.isVisible()) {
          wasActorBeforeDrop = false
          tryClaim(s)
        }
      }
    },
  }
}

// ============================================================================
// React 绑定（薄包装：只负责订阅与转发）
// ============================================================================

/** 抽出可见性来源，测试可注入；传 null 则整个 hook 关闭。 */
export interface VisibilitySource {
  isVisible(): boolean
  subscribe(cb: () => void): () => void
}

function domVisibility(): VisibilitySource | null {
  if (typeof document === 'undefined') return null
  return {
    isVisible: () => document.visibilityState === 'visible',
    subscribe: (cb) => {
      document.addEventListener('visibilitychange', cb)
      return () => {
        document.removeEventListener('visibilitychange', cb)
      }
    },
  }
}

export function useActorClaim(link: RoomLink, visibilityOverride?: VisibilitySource | null): void {
  const linkRef = useRef(link)
  linkRef.current = link

  useEffect(() => {
    const visibility = visibilityOverride === undefined ? domVisibility() : visibilityOverride
    if (visibility === null) return undefined

    const claimer = createActorClaimer({
      claim: () => {
        linkRef.current.send({ type: 'claimActor' })
      },
      isVisible: () => visibility.isVisible(),
    })

    const snap = (): ActorClaimSnapshot => {
      const s = useNetStore.getState()
      return { phase: s.phase, isActor: s.isActor, inRoom: s.view !== null }
    }

    claimer.onState(snap())
    const unsubVis = visibility.subscribe(() => {
      claimer.onVisibility()
    })
    const unsubStore = useNetStore.subscribe(() => {
      claimer.onState(snap())
    })

    return () => {
      unsubVis()
      unsubStore()
    }
  }, [visibilityOverride])
}
